import type { CreateRuleTargetDto, MetricsProtocol, RuleTargetAuthType, RuleTargetDto, RuleTargetKind } from '@citilight-monitor/contracts';
import { useEffect, useState } from 'react';
import { ApiError } from '../../lib/api';
import { PopupTemplate } from '../templates';
import type { FormFieldConfig } from './FormBuilder';
import { FormBuilder } from './FormBuilder';

interface RuleTargetFormValues extends Record<string, string> {
  name: string;
  kind: string;
  host: string;
  protocol: string;
  port: string;
  path: string;
  healthProtocol: string;
  healthPath: string;
  authType: string;
  authToken: string;
  authUsername: string;
  authPassword: string;
}

const EMPTY_VALUES: RuleTargetFormValues = {
  name: '',
  kind: '',
  host: '',
  protocol: 'http',
  port: '9100',
  path: '/metrics',
  healthProtocol: 'http',
  healthPath: '',
  authType: '',
  authToken: '',
  authUsername: '',
  authPassword: '',
};

function toValues(target: RuleTargetDto): RuleTargetFormValues {
  return {
    name: target.name,
    kind: target.kind,
    host: target.host,
    protocol: target.metricsEndpoint.protocol,
    port: String(target.port),
    path: target.metricsEndpoint.path,
    healthProtocol: target.healthEndpoint?.protocol ?? 'http',
    healthPath: target.healthEndpoint?.path ?? '',
    authType: target.auth?.type ?? 'none',
    authToken: target.auth?.token ?? '',
    authUsername: target.auth?.username ?? '',
    authPassword: target.auth?.password ?? '',
  };
}

function toDto(values: RuleTargetFormValues): CreateRuleTargetDto {
  const auth =
    values.authType === 'bearer'
      ? { type: 'bearer' as RuleTargetAuthType, token: values.authToken.trim() }
      : values.authType === 'basic'
        ? { type: 'basic' as RuleTargetAuthType, username: values.authUsername.trim(), password: values.authPassword.trim() }
        : undefined;
  const healthEndpoint = values.healthPath.trim()
    ? {
        protocol: values.healthProtocol as MetricsProtocol,
        path: values.healthPath.trim(),
      }
    : undefined;
  return {
    name: values.name.trim(),
    kind: values.kind as RuleTargetKind,
    host: values.host.trim(),
    port: Number(values.port),
    metricsEndpoint: {
      protocol: values.protocol as MetricsProtocol,
      path: values.path.trim() || '/metrics',
    },
    ...(healthEndpoint && { healthEndpoint }),
    ...(auth && { auth }),
  };
}

interface RuleTargetFormDialogProps {
  open: boolean;
  onClose: () => void;
  target?: RuleTargetDto;
  onSubmit: (dto: CreateRuleTargetDto) => Promise<unknown>;
}

export function RuleTargetFormDialog({ open, onClose, target, onSubmit }: RuleTargetFormDialogProps) {
  const [values, setValues] = useState<RuleTargetFormValues>(target ? toValues(target) : EMPTY_VALUES);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setValues(target ? toValues(target) : EMPTY_VALUES);
      setFormError(null);
    }
  }, [open, target]);

  const fields: FormFieldConfig<RuleTargetFormValues>[] = [
    { name: 'name', label: 'Name', validation: { required: true, maxLength: 200 } },
    {
      name: 'kind',
      label: 'Kind',
      type: 'select',
      options: [
        { value: 'SERVER', label: 'Server' },
        { value: 'APPLICATION', label: 'Application' },
      ],
      validation: { required: true },
    },
    {
      name: 'host',
      label: 'Host',
      hint: 'A LAN IP (192.168.50.33) or a domain (kafka.citilight.co)',
      validation: { required: true, maxLength: 255 },
    },
    {
      name: 'protocol',
      label: 'Protocol',
      type: 'select',
      options: [
        { value: 'http', label: 'http' },
        { value: 'https', label: 'https' },
      ],
      validation: { required: true },
    },
    { name: 'port', label: 'Application port', type: 'number', validation: { required: true, min: 1, max: 65535 } },
    { name: 'path', label: 'Metrics path', validation: { required: true, maxLength: 200 } },
    {
      name: 'healthProtocol',
      label: 'Health check protocol',
      type: 'select',
      options: [
        { value: 'http', label: 'http' },
        { value: 'https', label: 'https' },
      ],
    },
    {
      name: 'healthPath',
      label: 'Health check path (optional)',
      hint: 'Uses the application port above. Leave blank to use the metrics endpoint itself for reachability too.',
      validation: { maxLength: 200 },
    },
    {
      name: 'authType',
      label: 'Authentication',
      type: 'select',
      options: [
        { value: 'none', label: 'None' },
        { value: 'bearer', label: 'Bearer token' },
        { value: 'basic', label: 'Basic auth' },
      ],
      validation: { required: true },
    },
    ...(values.authType === 'bearer'
      ? [{ name: 'authToken', label: 'Token', validation: { required: true } } satisfies FormFieldConfig<RuleTargetFormValues>]
      : []),
    ...(values.authType === 'basic'
      ? [
          { name: 'authUsername', label: 'Username', validation: { required: true } } satisfies FormFieldConfig<RuleTargetFormValues>,
          {
            name: 'authPassword',
            label: 'Password',
            type: 'password',
            validation: { required: true },
          } satisfies FormFieldConfig<RuleTargetFormValues>,
        ]
      : []),
  ];

  async function handleSubmit() {
    setBusy(true);
    setFormError(null);
    try {
      await onSubmit(toDto(values));
      onClose();
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : 'Could not save this target.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <PopupTemplate
      open={open}
      onClose={onClose}
      title={target ? 'Edit target' : 'Add target'}
      description="A server (node_exporter) or an application exposing its own /metrics endpoint."
    >
      <FormBuilder
        fields={fields}
        values={values}
        onChange={(name, value) => setValues((v) => ({ ...v, [name]: value }))}
        onSubmit={handleSubmit}
        formError={formError}
        busy={busy}
        onCancel={onClose}
      />
    </PopupTemplate>
  );
}
